import { Box, Typography, Button } from "@mui/material";
import React from "react";
import { useTranslation } from "react-i18next";
import { getFileExtension } from "../../utils/files-utils/get-file-extension";

interface FilePickerWithDnDProps {
  onFileSelect: (file: File | null) => void;
  disabled?: boolean;
  allowedExtensions?: string[];
  children?: React.ReactNode;
}

const FilePickerWithDnD: React.FC<FilePickerWithDnDProps> = ({
  onFileSelect,
  disabled = false,
  allowedExtensions = [],
  children,
}) => {
  const inputRef = React.useRef<HTMLInputElement>(null);
  const [dragActive, setDragActive] = React.useState(false);
  const [selectedFile, setSelectedFile] = React.useState<File | null>(null);
  const tRP = "components.file_picker_with_dnd";
  const { t } = useTranslation();

  // Drag events for the box
  const handleDragEnter = (e: React.DragEvent<HTMLDivElement>) => {
    if (disabled) return;
    e.preventDefault();
    e.stopPropagation();
    setDragActive(true);
  };
  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    if (disabled) return;
    e.preventDefault();
    e.stopPropagation();
    setDragActive(true);
  };
  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    if (disabled) return;
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
  };
  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    if (disabled) return;
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    const file = e.dataTransfer.files[0];
    const fileExtension = getFileExtension(file.name);
    const extensionAllowed = allowedExtensions.length === 0 || allowedExtensions?.includes(fileExtension);
    if (extensionAllowed) {
      setSelectedFile(file);
      onFileSelect(file);
    } else {
      alert("File extension not allowed");
    }
  };

  // File input change
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (disabled) return;
    const file = e.target?.files ? e.target.files[0] : null;
    if (file) {
      const fileExtension = getFileExtension(file.name);
      const extensionAllowed = allowedExtensions.length === 0 || allowedExtensions?.includes(fileExtension);
      if (extensionAllowed) {
        setSelectedFile(file);
        onFileSelect(file);
      } else {
        alert(t(`${tRP}.file_extension_not_allowed`));
      }
    }
  };

  return (
    <Box
      sx={{
        border: disabled ? "2px dashed #e0e0e0" : dragActive ? "2px dashed #1976d2" : "2px dashed #ccc",
        borderRadius: 2,
        p: 2,
        textAlign: "center",
        cursor: disabled ? "not-allowed" : "pointer",
        background: disabled ? "#f5f5f5" : dragActive ? "#f0f6ff" : "transparent",
        position: "relative",
        opacity: disabled ? 0.6 : 1,
      }}
      onClick={() => !disabled && inputRef.current?.click()}
      onDragEnter={handleDragEnter}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      {children}

      <Typography
        variant="body2"
        sx={{ mb: 1, color: disabled ? "#9e9e9e" : "inherit" }}
      >
        {disabled ? `${t(`${tRP}.admin_access_required`)}` : `${t(`${tRP}.drag_n_drop_or_select`)}`}
      </Typography>
      <input
        ref={inputRef}
        type="file"
        style={{ display: "none" }}
        onChange={handleChange}
        disabled={disabled}
        {...(allowedExtensions && {
          accept: allowedExtensions.map((ext) => `.${ext}`).join(","),
        })}
      />

      <Button
        variant="outlined"
        disabled={disabled}
        onClick={(e) => {
          e.stopPropagation();
          if (!disabled) {
            inputRef.current?.click();
          }
        }}
      >
        {`${t(`${tRP}.choose_file`)}`}
      </Button>
      {selectedFile && (
        <Typography
          variant="body2"
          sx={{ fontWeight: 700, fontSize: "1.4 em", mt: 2 }}
        >
          {`${t(`${tRP}.selected_file`)} : ${selectedFile.name}`}
        </Typography>
      )}
      {dragActive && (
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            bgcolor: "rgba(25, 118, 210, 0.08)",
            zIndex: 10,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            pointerEvents: "none",
          }}
        >
          <Typography
            variant="h6"
            color="primary"
          >
            {`${t(`${tRP}.drop_file_here`)}`}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default FilePickerWithDnD;
